import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { priceService } from '../../services/priceService';
import { dietPriceService } from '../../services/dietPriceService';
import { DietPriceRow, Price } from '../../types/diet';
import Button from '../UI/Button';
import DietPriceModal from './DietPriceModal';

const allowedCategories = new Set(['Per Day','Per Meal','Half Board AM','Lunch Box','Half Board PM']);

interface DietPricesPanelProps {
  initialPriceId?: string;
  hidePriceSelector?: boolean;
}

const DietPricesPanel: React.FC<DietPricesPanelProps> = ({ initialPriceId, hidePriceSelector }) => {
  const qc = useQueryClient();
  const [priceId, setPriceId] = useState<string>(initialPriceId || '');
  const [rows, setRows] = useState<DietPriceRow[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => { if (initialPriceId) setPriceId(initialPriceId); }, [initialPriceId]);

  const { data: pricesData } = useQuery({
    queryKey: ['prices', 'all'],
    enabled: !hidePriceSelector,
    queryFn: () => priceService.getPrices({ page: 1, limit: 1000, status: 'ACTIVE', sortBy: 'name' }),
  });
  const priceOptions: Price[] = useMemo(() => (pricesData?.prices || []).filter(p => allowedCategories.has(p.name)), [pricesData]);

  const { data: dietPricesData, isFetching } = useQuery({
    queryKey: ['diet-prices', priceId],
    enabled: !!priceId,
    queryFn: () => dietPriceService.getDietPrices(priceId),
  });

  useEffect(() => {
    if (dietPricesData?.items) setRows(dietPricesData.items);
  }, [dietPricesData]);

  const saveMutation = useMutation({
    mutationFn: () => dietPriceService.bulkUpsert(priceId, rows.map(r => ({
      id: r.id,
      dietId: r.dietId,
      pricePerDay: r.pricePerDay,
      currency: r.currency,
      periodDays: r.periodDays,
      totalPrice: r.totalPrice,
      notes: r.notes,
      status: r.status,
    }))),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diet-prices', priceId] });
    }
  });

  const handleFieldChange = (idx: number, field: keyof DietPriceRow, value: any) => {
    setRows(prev => {
      const next = [...prev];
      const r = { ...next[idx] } as DietPriceRow;
      if (field === 'pricePerDay' || field === 'periodDays') {
        const vNum = Number(value || 0);
        (r as any)[field] = vNum;
        // auto-calc total when price/day or period changes
        const p = field === 'pricePerDay' ? vNum : (r.pricePerDay || 0);
        const d = field === 'periodDays' ? vNum : (r.periodDays || 0);
        r.totalPrice = Number((p * d).toFixed(2));
      } else if (field === 'totalPrice') {
        r.totalPrice = Number(value || 0);
      } else if (field === 'status') {
        r.status = value as any;
      } else if (field === 'currency') {
        r.currency = String(value || 'USD');
      } else if (field === 'notes') {
        r.notes = String(value || '');
      }
      next[idx] = r;
      return next;
    });
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    await dietPriceService.delete(id);
    qc.invalidateQueries({ queryKey: ['diet-prices', priceId] });
  };

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="md:flex md:items-end md:justify-between gap-4">
        {!hidePriceSelector && (
          <div className="w-full md:w-80">
            <label className="form-label">Price Category *</label>
            <select className="form-input h-11 text-base" value={priceId} onChange={e => setPriceId(e.target.value)}>
              <option value="">Select</option>
              {priceOptions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex gap-3 mt-3 md:mt-0">
          <Button variant="secondary" onClick={() => setIsCreateOpen(true)} disabled={!priceId}>Create</Button>
          <Button variant="secondary" onClick={async ()=>{
            if (!priceId) return;
            try {
              const blob = await dietPriceService.export(priceId);
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a'); a.style.display='none'; a.href=url; a.download='diet-prices-' + priceId + '.xlsx';
              document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
            } catch(e){ console.error('Export failed', e);}
          }} disabled={!priceId}>Export to Excel</Button>
          <Button onClick={() => saveMutation.mutate()} disabled={!priceId || rows.length === 0} loading={saveMutation.isPending}>Save</Button>
        </div>
      </div>

      {priceId && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full border rounded-lg whitespace-nowrap text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left">Diet</th>
                <th className="px-3 py-3 text-left">Price/Day</th>
                <th className="px-3 py-3 text-left">Currency</th>
                <th className="px-3 py-3 text-left">Period (Days)</th>
                <th className="px-3 py-3 text-left">Total Price</th>
                <th className="px-3 py-3 text-left">Notes</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 text-left">Status Date</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && (
                <tr><td colSpan={9} className="px-3 py-3 text-center text-gray-500">Loading...</td></tr>
              )}
              {!isFetching && rows.map((r, idx) => (
                <tr key={r.dietId} className="border-t">
                  <td className="px-3 py-3 font-medium">{r.dietName}</td>
                  <td className="px-3 py-3">
                    <input type="number" step="0.01" className="form-input h-11 text-base w-32" value={r.pricePerDay}
                      onChange={e => handleFieldChange(idx, 'pricePerDay', e.target.value)} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="text" className="form-input h-11 text-base w-28" value={r.currency || 'USD'}
                      onChange={e => handleFieldChange(idx, 'currency', e.target.value)} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="number" className="form-input h-11 text-base w-28" value={r.periodDays}
                      onChange={e => handleFieldChange(idx, 'periodDays', e.target.value)} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="number" step="0.01" className="form-input h-11 text-base w-32" value={r.totalPrice}
                      onChange={e => handleFieldChange(idx, 'totalPrice', e.target.value)} />
                  </td>
                  <td className="px-3 py-3">
                    <input type="text" className="form-input h-11 text-base min-w-[16rem]" value={r.notes || ''}
                      onChange={e => handleFieldChange(idx, 'notes', e.target.value)} />
                  </td>
                  <td className="px-3 py-3">
                    <select className="form-input h-11 text-base w-32" value={r.status}
                      onChange={e => handleFieldChange(idx, 'status', e.target.value)}>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </td>
                  <td className="px-3 py-3 text-gray-500">{r.statusDate ? new Date(r.statusDate).toLocaleString() : '-'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="danger" size="sm" onClick={() => handleDelete(r.id)} disabled={!r.id}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreateOpen && (
        <DietPriceModal priceId={priceId} onClose={() => setIsCreateOpen(false)} onSuccess={() => { setIsCreateOpen(false); qc.invalidateQueries({ queryKey: ['diet-prices', priceId] }); }} />
      )}
    </div>
  );
};

export default DietPricesPanel;

